import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type SuggestedUser = {
  id: string;
  name: string;
  avatarUrl?: string;
  city?: string;
  crops?: string[];
  mutualTeams?: number;
  overlapScore?: number;
};

export function useSuggestedUsers(limit: number = 20) {
  return useQuery({
    queryKey: ["suggested-users"],
    queryFn: async () => {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) return [];

      const userId = currentUser.data.user.id;

      // Get current user's profile
      const { data: currentProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", userId)
        .single();

      if (!currentProfile) return [];

      // Get current user's communities
      const { data: userCommunities } = await supabase
        .from("user_communities")
        .select("community_id")
        .eq("profile_id", currentProfile.id);

      const userCommunityIds = new Set(userCommunities?.map(uc => uc.community_id) || []);

      // Get other users
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select(`
          id,
          username,
          avatar_url,
          location,
          user_id
        `)
        .eq("is_active", true)
        .eq("is_discoverable", true)
        .neq("user_id", userId)
        .limit(100);

      if (error) throw error;
      if (!profiles) return [];

      // Get their communities
      const profileIds = profiles.map(p => p.id);
      const { data: otherCommunities } = await supabase
        .from("user_communities")
        .select("profile_id, community_id")
        .in("profile_id", profileIds);

      // Calculate overlap scores
      const communityMap = new Map<string, string[]>();
      otherCommunities?.forEach(uc => {
        const communities = communityMap.get(uc.profile_id) || [];
        communities.push(uc.community_id);
        communityMap.set(uc.profile_id, communities);
      });

      const suggested: SuggestedUser[] = profiles
        .map(profile => {
          const theirCommunities = new Set(communityMap.get(profile.id) || []);
          
          // Calculate Jaccard similarity
          const intersection = [...userCommunityIds].filter(c => theirCommunities.has(c)).length;
          const union = new Set([...userCommunityIds, ...theirCommunities]).size;
          const jaccardScore = union > 0 ? intersection / union : 0;

          // Count mutual teams
          const mutualTeams = intersection;

          return {
            id: profile.user_id,
            name: profile.username || "Farmer",
            avatarUrl: profile.avatar_url || undefined,
            city: profile.location || undefined,
            crops: [], // TODO: fetch from communities or profile_tags
            mutualTeams,
            overlapScore: jaccardScore,
          };
        })
        .filter(u => u.overlapScore > 0.3 || u.mutualTeams > 0)
        .sort((a, b) => {
          // Sort by overlap score, then mutual teams, then name
          if (b.overlapScore !== a.overlapScore) {
            return (b.overlapScore || 0) - (a.overlapScore || 0);
          }
          if (b.mutualTeams !== a.mutualTeams) {
            return (b.mutualTeams || 0) - (a.mutualTeams || 0);
          }
          return a.name.localeCompare(b.name);
        })
        .slice(0, limit);

      return suggested;
    },
    enabled: true,
  });
}
